import ExcelJS from 'exceljs'
import { saveAs } from 'file-saver'
import _ from 'lodash'
import moment from 'moment'
import { getCrm, numberFormat, toBuddhistYear } from 'utils/app.utils'
import { forceTextOnDateCells } from 'utils/excel.utils'

interface DentistInterface {
    dentistId: number
    dentistFullname: string
}

async function ExportToExcel({
    data,
    doctors,
    filter,
    filterDateStart,
    filterDateEnd,
    branchName,
    t,
}: {
    data: any
    doctors: DentistInterface[]
    filter: any
    filterDateStart: string
    filterDateEnd: string
    branchName: string
    t: any
}) {
    const hasCrm = getCrm()
    const workbook = new ExcelJS.Workbook()
    const sheet = workbook.addWorksheet(t('REPORT.REPORT_NAME.REMUNERATION_ALL_DENTIST'))

    // Title
    const titleRow = sheet.addRow([t('REPORT.REPORT_NAME.REMUNERATION_ALL_DENTIST')])
    titleRow.font = { bold: true, size: 16 }
    sheet.mergeCells(titleRow.number, 1, titleRow.number, 22)
    titleRow.alignment = { horizontal: 'left' }

    // Filter information
    const filterTitles = [
        { key: 'REPORT.FILTER.BRANCH', label: 'REPORT.FILTER.BRANCH' },
        { key: 'REPORT.FILTER.DATE_START', label: 'REPORT.FILTER.DATE_START' },
        { key: 'REPORT.FILTER.DATE_END', label: 'REPORT.FILTER.DATE_END' },
        { key: 'REPORT.FILTER.RECEIPT_TYPE', label: 'REPORT.FILTER.RECEIPT_TYPE' },
        { key: 'REPORT.FILTER.DOCTOR', label: 'REPORT.FILTER.DOCTOR' }
    ]

    const renderValue = (value: any, index: any) => {
        if (index === 1 || index === 2) {
            return toBuddhistYear(moment(value), 'DD/MM/YYYY')
        }
        return value
    }

    const filterRowData: any[] = []

    filterTitles.forEach((filterItem, index) => {
        const label = `${t(filterItem.label)}:`
        const value = renderValue(filter[index], index)
        filterRowData.push(label, value)
    })

    const filterRow = sheet.addRow(filterRowData)
    filterRow.font = { size: 10 }

    for (let col = 1; col <= filterRowData.length; col += 2) {
        filterRow.getCell(col).font = { bold: true }
        filterRow.getCell(col).alignment = { horizontal: 'left' }
        filterRow.getCell(col + 1).alignment = { horizontal: 'left' }
    }

    // Doctor Fee Table
    const doctorFeeTitleRow = sheet.addRow([t('REPORT.TABLE.DOCTOR_FEE')])
    doctorFeeTitleRow.font = { bold: true, size: 14 }
    doctorFeeTitleRow.alignment = { horizontal: 'left' }

    const doctorFeeHeaders = [
        { label: t('REPORT.TABLE.REMUNERATION.#NO'), width: 8 },
        { label: t('REPORT.TABLE.REMUNERATION.BILL_ID'), width: 15 },
        { label: t('REPORT.TABLE.REMUNERATION.DOCTOR'), width: 20 },
        { label: t('REPORT.TABLE.REMUNERATION.PATIENT_CN_NUMBER'), width: 15 },
        { label: t('REPORT.TABLE.REMUNERATION.PATIENT_SN_CODE'), width: 15 },
        { label: t('REPORT.TABLE.REMUNERATION.PATIENT_NAME'), width: 20 },
        { label: t('REPORT.TABLE.REMUNERATION.TX'), width: 12 },
        { label: t('REPORT.TABLE.REMUNERATION.MED'), width: 12 },
        { label: t('REPORT.TABLE.REMUNERATION.PD'), width: 12 },
        { label: t('REPORT.TABLE.REMUNERATION.MAT'), width: 12 },
        { label: t('REPORT.TABLE.REMUNERATION.PAYMENT'), width: 12 },
        { label: t('REPORT.TABLE.REMUNERATION.CASH'), width: 12 },
        { label: t('REPORT.TABLE.REMUNERATION.TRANSFER'), width: 12 },
        { label: t('REPORT.TABLE.REMUNERATION.DEBIT'), width: 12 },
        { label: t('REPORT.TABLE.REMUNERATION.CREDIT'), width: 12 },
        { label: t('REPORT.TABLE.REMUNERATION.TREATMENT_RIGHT'), width: 12 },
        ...(hasCrm ? [
            { label: t('REPORT.TABLE.REMUNERATION.VOUCHER'), width: 12 },
            { label: t('REPORT.TABLE.REMUNERATION.PROMOTION'), width: 12 },
        ] : []),
        { label: t('REPORT.TABLE.REMUNERATION.ADVANCE'), width: 12 },
        { label: t('REPORT.TABLE.REMUNERATION.DF'), width: 12 },
        { label: t('REPORT.TABLE.REMUNERATION.CREDIT_DF'), width: 12 },
        { label: t('REPORT.TABLE.REMUNERATION.TREATMENT_RIGHT_DF'), width: 12 },
        { label: t('REPORT.TABLE.REMUNERATION.NET_DF'), width: 12 }
    ]

    sheet.columns = doctorFeeHeaders.map(c => ({ width: c.width }))
    const doctorFeeHeaderRow = sheet.addRow(doctorFeeHeaders.map(c => c.label))
    doctorFeeHeaderRow.font = { bold: true, size: 10 }
    doctorFeeHeaderRow.alignment = { horizontal: 'center', vertical: 'middle' }

    // Add doctor fee data
    if (data.rows && data.rows.length > 0) {
        data.rows.map((salary: any) => {
            // Doctor name row
            const doctorNameRow = sheet.addRow([`${t('REPORT.FILTER.DOCTOR')} ${_.get(_.find(doctors, { dentistId: Number(salary.table[0].doctorId) }), 'dentistFullname')}`])
            doctorNameRow.font = { bold: true, size: 10 }
            sheet.mergeCells(doctorNameRow.number, 1, doctorNameRow.number, 22)
            doctorNameRow.alignment = { horizontal: 'left' }

            // Group by date
            const renderSalaryTable = (data: any) => {
                const items = _.chain(data)
                    .map((i: any) => {
                        i.dateFormat = moment(i.date)
                        return i
                    })
                    .groupBy('dateFormat')
                    .map((value: any, key: string) => ({ date: key, data: value }))
                    .value()
                return items
            }

            renderSalaryTable(salary.table).forEach((d: any) => {
                // Date row
                const dateRow = sheet.addRow([`${t('MOMENT_AT')} ${toBuddhistYear(moment(d.date), 'DD/MM/YYYY')}`])
                dateRow.font = { bold: true, size: 10 }
                sheet.mergeCells(dateRow.number, 1, dateRow.number, 22)
                dateRow.alignment = { horizontal: 'left' }

                // Data rows
                d.data.forEach((dataTable: any, indexTable: number) => {
                    const {
                        stage,
                        billNo,
                        branchCnNumber,
                        snCode,
                        patientFullname,
                        paidTx,
                        paidMed,
                        paidPd,
                        amountPaid,
                        amountCash,
                        amountTransfer,
                        amountDebit,
                        amountCredit,
                        amountTreatmentRight,
                        amountVoucher,
                        amountPromotion,
                        amountAdvance,
                        dfPaid,
                        dfLostTreatment,
                        creditCost,
                        dfNet,
                        doctorId,
                        paidMat
                    } = dataTable

                    sheet.addRow([
                        indexTable + 1,
                        stage > 1 ? `${billNo} ${t('REPORT.TABLE.REMUNERATION.IS_PAY_OVERDUE')}` : (billNo || '-'),
                        _.get(_.find(doctors, { dentistId: Number(doctorId) }), 'dentistFullname') || '-',
                        branchCnNumber || '-',
                        snCode || '-',
                        patientFullname || '-',
                        numberFormat(paidTx || 0),
                        numberFormat(paidMed || 0),
                        numberFormat(paidPd || 0),
                        numberFormat(paidMat || 0),
                        numberFormat(amountPaid || 0),
                        numberFormat(amountCash || 0),
                        numberFormat(amountTransfer || 0),
                        numberFormat(amountDebit || 0),
                        numberFormat(amountCredit || 0),
                        numberFormat(amountTreatmentRight || 0),
                        ...(hasCrm ? [
                            numberFormat(amountVoucher || 0),
                            numberFormat(amountPromotion || 0),
                        ] : []),
                        numberFormat(amountAdvance || 0),
                        numberFormat(dfPaid || 0),
                        numberFormat(creditCost || 0, 3, 3),
                        numberFormat(dfLostTreatment || 0),
                        numberFormat(dfNet || 0)
                    ])
                })

                // Summary row for this date
                const summaryRow = sheet.addRow([
                    '', '', '', '', '', t('REPORT.TITLE.SUM'),
                    numberFormat(_.sumBy(_.filter(d.data, { stage: 1 }), 'paidTx')),
                    numberFormat(_.sumBy(_.filter(d.data, { stage: 1 }), 'paidMed')),
                    numberFormat(_.sumBy(_.filter(d.data, { stage: 1 }), 'paidPd')),
                    numberFormat(_.sumBy(_.filter(d.data, { stage: 1 }), 'paidMat')),
                    numberFormat(_.sumBy(d.data, 'amountPaid')),
                    numberFormat(_.sumBy(d.data, 'amountCash')),
                    numberFormat(_.sumBy(d.data, 'amountTransfer')),
                    numberFormat(_.sumBy(d.data, 'amountDebit')),
                    numberFormat(_.sumBy(d.data, 'amountCredit')),
                    numberFormat(_.sumBy(d.data, 'amountTreatmentRight')),
                    ...(hasCrm ? [
                        numberFormat(_.sumBy(d.data, 'amountVoucher')),
                        numberFormat(_.sumBy(d.data, 'amountPromotion')),
                    ] : []),
                    numberFormat(_.sumBy(d.data, 'amountAdvance')),
                    numberFormat(_.sumBy(d.data, 'dfPaid')),
                    numberFormat(_.sumBy(d.data, 'creditCost'), 3, 3),
                    numberFormat(_.sumBy(d.data, 'dfLostTreatment')),
                    numberFormat(_.sumBy(d.data, 'dfNet'))
                ])
                summaryRow.font = { bold: true }
                summaryRow.alignment = { horizontal: 'right' }
            })
        })
    } else {
        const noDataRow = sheet.addRow([t('REPORT.TITLE.NO_DATA')])
        sheet.mergeCells(noDataRow.number, 1, noDataRow.number, 22)
        noDataRow.font = { italic: true, color: { argb: 'FF000000' } }
        noDataRow.alignment = { horizontal: 'center' }
    }

    // Add some spacing
    sheet.addRow([])
    sheet.addRow([])

    // Lab Report Table
    const labTitleRow = sheet.addRow(['LAB Report'])
    labTitleRow.font = { bold: true, size: 14 }
    labTitleRow.alignment = { horizontal: 'left' }

    const labHeaders = [
        { label: t('REPORT.TABLE.REMUNERATION.#NO') },
        { label: t('REPORT.TABLE.REMUNERATION.DOCTOR') },
        { label: t('REPORT.TABLE.REMUNERATION.PATIENT_CN_NUMBER') },
        { label: t('REPORT.TABLE.REMUNERATION.PATIENT_SN_CODE') },
        { label: t('REPORT.TABLE.REMUNERATION.PATIENT_NAME') },
        { label: t('REPORT.TABLE.REMUNERATION.DESCRIPTION') },
        { label: t('REPORT.TABLE.REMUNERATION.LAB_NAME') },
        { label: t('REPORT.TABLE.REMUNERATION.JOB') },
        { label: 'LAB Fee' },
        { label: 'DF LAB Fee' }
    ]

    const labHeaderRow = sheet.addRow(labHeaders.map(c => c.label))
    labHeaderRow.font = { bold: true, size: 10 }
    labHeaderRow.alignment = { horizontal: 'center', vertical: 'middle' }

    // Add lab data
    if (data.rowLab && data.rowLab.length > 0) {
        data.rowLab.forEach((d: any) => {
            // Date row
            const dateRow = sheet.addRow([`${t('MOMENT_AT')} ${toBuddhistYear(moment(d.date), 'DD/MM/YYYY')}`])
            dateRow.font = { bold: true, size: 10 }
            sheet.mergeCells(dateRow.number, 1, dateRow.number, 10)
            dateRow.alignment = { horizontal: 'left' }

            // Data rows
            d.table.forEach((dataTable: any, indexTable: number) => {
                const { dentist, branchCnNumber, snCode, patientFullname, remark, lab, operativeName, labFee, dfLabFee } = dataTable
                sheet.addRow([
                    indexTable + 1,
                    dentist || '-',
                    branchCnNumber || '-',
                    snCode || '-',
                    patientFullname || '-',
                    remark || '-',
                    lab || '-',
                    operativeName || '-',
                    numberFormat(labFee || 0),
                    numberFormat(dfLabFee || 0)
                ])
            })

            // Summary row for this date
            const summaryRow = sheet.addRow([
                '', '', '', '', '', '', '', t('REPORT.TITLE.SUM'),
                numberFormat(_.sumBy(d.table, 'labFee')),
                numberFormat(_.sumBy(d.table, 'dfLabFee'))
            ])
            summaryRow.font = { bold: true }
            summaryRow.alignment = { horizontal: 'right' }
        })
    }

    // Add some spacing
    sheet.addRow([])
    sheet.addRow([])

    // Dentist Free Table
    const dfTitleRow = sheet.addRow([t('REPORT.TABLE.REMUNERATION.DENTIST_FREE')])
    dfTitleRow.font = { bold: true, size: 14 }
    dfTitleRow.alignment = { horizontal: 'left' }

    const dfHeaders = [
        { label: t('REPORT.TABLE.REMUNERATION.#NO') },
        { label: t('REPORT.TABLE.REMUNERATION.DOCTOR') },
        { label: t('REPORT.TABLE.REMUNERATION.NET_PRICE') },
        { label: 'ค่าประกันมือ' },
        { label: t('REPORT.TABLE.REMUNERATION.SUM_PAID') },
        { label: t('DF') },
        { label: t('REPORT.TABLE.REMUNERATION.CREDIT_DF') },
        { label: t('REPORT.TABLE.REMUNERATION.TREATMENT_RIGHT_DF') },
        { label: t('REPORT.TABLE.REMUNERATION.SUM_DF') },
        { label: 'LAB Fee' },
        { label: 'DF LAB Fee' }
    ]

    const dfHeaderRow = sheet.addRow(dfHeaders.map(c => c.label))
    dfHeaderRow.font = { bold: true, size: 10 }
    dfHeaderRow.alignment = { horizontal: 'center', vertical: 'middle' }

    // Add DF data
    if (data.rowDf && data.rowDf.length > 0) {
        data.rowDf.forEach((d: any, indexTable: number) => {
            const { doctorId, totalAmount, amountPaid, amountDf, dfCredit, dfLabFee, dfNet, dfLostCreditAndTreatment, dfLostTreatment, handInsurance } = d
            const totalDFLab = (Number(dfLostCreditAndTreatment) || 0) - (Number(dfNet) || 0)
            const newDFLab = totalDFLab < 0 ? 0 : totalDFLab

            sheet.addRow([
                indexTable + 1,
                _.get(_.find(doctors, { dentistId: Number(doctorId) }), 'dentistFullname') || '-',
                numberFormat(totalAmount || 0),
                numberFormat(handInsurance || 0),
                numberFormat(amountPaid || 0),
                numberFormat(amountDf || 0),
                numberFormat(dfCredit || 0),
                numberFormat(dfLostTreatment || 0),
                numberFormat(dfLostCreditAndTreatment || 0),
                numberFormat(newDFLab || 0),
                numberFormat(dfNet || 0)
            ])
        })

        // Summary row
        const totaSumlDFLab = data.rowDf?.length ? data.rowDf.map((item: any) => {
            const newSum = (item?.dfLostCreditAndTreatment || 0) - (item?.dfNet || 0)
            if (newSum < 0) {
                return 0
            } else {
                return newSum
            }
        }).reduce((a: any, b: any) => Number(a || 0) + Number(b || 0)) : 0
        const newSumDFLab = totaSumlDFLab < 0 ? 0 : totaSumlDFLab

        const summaryRow = sheet.addRow([
            '', t('REPORT.TITLE.SUM'),
            numberFormat(_.sumBy(data.rowDf, 'totalAmount')),
            numberFormat(_.sumBy(data.rowDf, 'handInsurance')),
            numberFormat(_.sumBy(data.rowDf, 'amountPaid')),
            numberFormat(_.sumBy(data.rowDf, 'amountDf')),
            numberFormat(_.sumBy(data.rowDf, 'dfCredit')),
            numberFormat(_.sumBy(data.rowDf, 'dfLostTreatment')),
            numberFormat(_.sumBy(data.rowDf, 'dfLostCreditAndTreatment')),
            numberFormat(newSumDFLab),
            numberFormat(_.sumBy(data.rowDf, 'dfNet'))
        ])
        summaryRow.font = { bold: true }
        summaryRow.alignment = { horizontal: 'right' }
    }

    // Add some spacing
    sheet.addRow([])
    sheet.addRow([])

    // Total Income Summary Table
    const totalTitleRow = sheet.addRow(['Total Income Summary'])
    totalTitleRow.font = { bold: true, size: 14 }
    totalTitleRow.alignment = { horizontal: 'left' }

    const totalHeaders = [
        { label: t('REPORT.TABLE.REMUNERATION.INCOME') },
        { label: t('REPORT.TABLE.REMUNERATION.NET_CLINIC') },
        { label: t('REPORT.TABLE.REMUNERATION.DF_NET') },
        { label: t('REPORT.TABLE.REMUNERATION.SUM_DF') },
        { label: t('REPORT.TABLE.REMUNERATION.DF_LAB') },
        { label: t('REPORT.TABLE.REMUNERATION.CASH') },
        { label: t('REPORT.TABLE.REMUNERATION.TRANSFER') },
        { label: t('REPORT.TABLE.REMUNERATION.DEBIT') },
        { label: t('REPORT.TABLE.REMUNERATION.CREDIT') },
        { label: t('REPORT.TABLE.REMUNERATION.TREATMENT_RIGHT') },
        ...(hasCrm ? [
            { label: t('REPORT.TABLE.REMUNERATION.VOUCHER') },
            { label: t('REPORT.TABLE.REMUNERATION.PROMOTION') },
        ] : []),
        { label: t('REPORT.TABLE.REMUNERATION.ADVANCE') }
    ]

    const totalHeaderRow = sheet.addRow(totalHeaders.map(c => c.label))
    totalHeaderRow.font = { bold: true, size: 10 }
    totalHeaderRow.alignment = { horizontal: 'center', vertical: 'middle' }

    // Calculate totals
    let sumAmountCash = 0
    let sumAmountTransfer = 0
    let sumAmountDebit = 0
    let sumAmountCredit = 0
    let sumAmountTrementRight = 0
    let sumAmountVoucher = 0
    let sumAmountPromotion = 0
    let sumAmountAdvance = 0
    let sumLab = 0
    let sumAmountPaid = 0
    let dfNet = 0
    let sumDf = 0
    let sumDfNet = 0
    let dfLabFee = 0

    _.map(data.rows, (salary, indexSalary: number) => {
        sumAmountPaid += salary.table.map((amountPaidData: any) => amountPaidData.amountPaid).reduce((a: any, b: any) => Number(a || 0) + Number(b || 0))
        sumAmountCash += salary.table.map((amountCashData: any) => amountCashData.amountCash).reduce((a: any, b: any) => Number(a || 0) + Number(b || 0))
        sumAmountTransfer += salary.table.map((amountTransferData: any) => amountTransferData.amountTransfer).reduce((a: any, b: any) => Number(a || 0) + Number(b || 0))
        sumAmountDebit += salary.table.map((amountDebitData: any) => amountDebitData.amountDebit).reduce((a: any, b: any) => Number(a || 0) + Number(b || 0))
        sumAmountCredit += salary.table.map((amountCreditData: any) => amountCreditData.amountCredit).reduce((a: any, b: any) => Number(a || 0) + Number(b || 0))
        sumAmountTrementRight += salary.table.map((amountTreatmentRightData: any) => amountTreatmentRightData.amountTreatmentRight).reduce((a: any, b: any) => Number(a || 0) + Number(b || 0))
        sumAmountVoucher += salary.table.map((row: any) => row.amountVoucher).reduce((a: any, b: any) => Number(a || 0) + Number(b || 0), 0)
        sumAmountPromotion += salary.table.map((row: any) => row.amountPromotion).reduce((a: any, b: any) => Number(a || 0) + Number(b || 0), 0)
        sumAmountAdvance += salary.table.map((amountAdvanceData: any) => amountAdvanceData.amountAdvance).reduce((a: any, b: any) => Number(a || 0) + Number(b || 0))
    })

    _.map(data.rowLab, (lab) => {
        sumLab += lab.table.map((sumLabData: any) => sumLabData.labFee).reduce((a: any, b: any) => Number(a || 0) + Number(b || 0))
    })

    if (data.rowDf !== undefined) {
        if (data.rowDf.length > 0) {
            sumDf += data.rowDf ? data.rowDf.map((rowDfData: any) => rowDfData.dfLostCreditAndTreatment).reduce((a: any, b: any) => Number(a || 0) + Number(b || 0)) : 0
            dfNet += data.rowDf ? data.rowDf.map((rowDfData: any) => rowDfData.dfNet).reduce((a: any, b: any) => Number(a || 0) + Number(b || 0)) : 0
            sumDfNet += data.rowDf ? data.rowDf.map((rowDfData: any) => rowDfData.dfNet).reduce((a: any, b: any) => Number(a || 0) + Number(b || 0)) : 0
            dfLabFee += data.rowDf ? data.rowDf.map((rowDfData: any) => rowDfData.dfLabFee).reduce((a: any, b: any) => Number(a || 0) + Number(b || 0)) : 0
        }
    }

    const totaSumlDFLab = data.rowDf?.length ? data.rowDf.map((item: any) => {
        const newSum = (item?.dfLostCreditAndTreatment || 0) - (item?.dfNet || 0)
        if (newSum < 0) {
            return 0
        } else {
            return newSum
        }
    }).reduce((a: any, b: any) => Number(a || 0) + Number(b || 0)) : 0
    const newSumDFLab = totaSumlDFLab < 0 ? 0 : totaSumlDFLab

    const netClinic = (sumAmountPaid - dfNet) < 0 ? 0 : (sumAmountPaid - dfNet)

    sheet.addRow([
        numberFormat(sumAmountPaid),
        numberFormat(netClinic),
        numberFormat(dfNet),
        numberFormat(dfNet + dfLabFee),
        numberFormat(newSumDFLab),
        numberFormat(sumAmountCash),
        numberFormat(sumAmountTransfer),
        numberFormat(sumAmountDebit),
        numberFormat(sumAmountCredit),
        numberFormat(sumAmountTrementRight),
        ...(hasCrm ? [
            numberFormat(sumAmountVoucher),
            numberFormat(sumAmountPromotion),
        ] : []),
        numberFormat(sumAmountAdvance)
    ])

    // Save file
    forceTextOnDateCells(workbook)
    const buffer = await workbook.xlsx.writeBuffer()
    const timestamp = new Date().toISOString().replace(/[-:T.]/g, '').slice(0, 14)
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
    saveAs(blob, `${t('REPORT.REPORT_NAME.REMUNERATION_ALL_DENTIST')}_${timestamp}.xlsx`)
}

export default ExportToExcel

